import { Product, ProductSlug } from "@/api/product-api";
import { CartItem, CartItemOption } from "@/store/cart-store";
import type { ProductInfo } from "@/services/cart-service/types";
import { ProductUtil } from "@/utils/ProductUtil";
import { CurrencyIsoCode } from "@/enums";

export class CartService {
  public static getCandidateBundles({
    candidate,
    cartItems,
    products,
  }: {
    candidate: Product;
    cartItems: CartItem[];
    products: Product[];
  }): Product[] {
    const deviceBundles = ProductUtil.getDeviceBundles(products, candidate);
    if (deviceBundles.length === 0) return [];

    const candidateSkus = new Set(
      candidate.skuOptions.flatMap((opt) =>
        opt.components.length > 0 ? opt.components : [opt.sku],
      ),
    );

    // 카트 아이템의 SKU 집합 (번들이면 구성품으로 확장)
    const cartSkus = new Set<string>();
    for (const item of cartItems) {
      cartSkus.add(item.sku);
      // 카트 아이템이 번들이면 구성품 SKU도 추가
      const cartProduct = products.find((p) => p.slug === item.slug);
      if (cartProduct) {
        const opt = cartProduct.skuOptions.find((o) => o.sku === item.sku);
        if (opt && opt.components.length > 0) {
          opt.components.forEach((sku) => cartSkus.add(sku));
        }
      }
    }

    // candidate + cartItems 조합이 번들을 만들 수 있으면 [] (자동 병합 가능)
    const canAutoMerge = deviceBundles.some((bundle) =>
      bundle.skuOptions.some((opt) => {
        const remaining = opt.components.filter(
          (sku) => !candidateSkus.has(sku),
        );
        return (
          remaining.length > 0 && remaining.every((sku) => cartSkus.has(sku))
        );
      }),
    );

    if (canAutoMerge) return [];

    return deviceBundles;
  }
  public static getProductBySlug({
    target,
    products,
  }: {
    target: ProductSlug;
    products: Product[];
  }): Product | null {
    const found = products.find((product) => product.slug === target);

    return found ?? null;
  }

  public static getProductsBySlug({
    target,
    products,
  }: {
    target: ProductSlug[];
    products: Product[];
  }): Product[] {
    return products.filter((product) => target.includes(product.slug));
  }

  public static getProductInfoList({
    cartItems,
    products,
  }: {
    cartItems: CartItem[];
    products: Product[];
    countryCode?: string | null;
  }): ProductInfo[] {
    const productInfoList = cartItems
      .map((cartProduct) => {
        let product = products.find(
          (product) => product.slug === cartProduct.slug,
        );

        if (!product) {
          return null;
        }
        const productImages = ProductUtil.getProductImages({
          product,
          sku: cartProduct.sku,
          options: cartProduct.options,
        });
        product = {
          ...product,
          productImages,
        };

        return { ...product, ...cartProduct };
      })
      .filter((item): item is ProductInfo => item !== null);

    // 카트에 있는 모든 아이템 중 가장 늦은 shipping date 정보를 넣어준다.
    // 옵션별 shippingInfo를 사용
    if (productInfoList.length > 0) {
      const shippingDates = productInfoList
        .map((item) => {
          const skuOption = item.skuOptions.find((opt) => opt.sku === item.sku);
          return ProductUtil.getSkuShippingDate(skuOption);
        })
        .filter((date): date is string => !!date);

      if (shippingDates.length > 0) {
        const finalShippingDate = shippingDates.sort(
          (a, b) => new Date(b).getTime() - new Date(a).getTime(),
        )[0];

        // 모든 productInfo에 finalShippingDate에 가장 늦은 배송일 정보를 추가로 넣는다.
        productInfoList.forEach((productInfo) => {
          productInfo.finalShippingDate = finalShippingDate;
        });
      }
    }

    return productInfoList;
  }

  /**
   * 카트 항목 중 현재 product 리스트에서 렌더 불가(ghost)한 것 — slug 미해석 또는 현재
   * currency 로 가격이 null 인 항목. 카운트(localStorage)·배송비(sku passthrough)는 남는데
   * 행/가격이 안 잡혀 "1개인데 안 보이고 $0" 가 되는 항목으로, UI 에서 별도 노출 + 삭제 유도 +
   * 결제 차단에 쓴다. CartItem 의 `if (!price) return null` 렌더 기준과 동일.
   *
   * ⚠️ 목록 미로드/빈 응답(productsPending 이거나 products 가 빈 배열)일 땐 [] 를 반환 —
   * 그 순간 전부 ghost 로 오인해 정상 항목을 "이용 불가" 로 떨구는 것을 막는다.
   */
  public static getUnresolvedItems({
    items,
    products,
    productsPending,
    currency,
    countryCode,
  }: {
    items: CartItem[];
    products: Product[];
    productsPending: boolean;
    currency: CurrencyIsoCode;
    countryCode?: string | null;
  }): CartItem[] {
    if (productsPending || products.length === 0) {
      return [];
    }
    const bySlug = new Map(products.map((p) => [p.slug, p]));
    return items.filter((item) => {
      const product = bySlug.get(item.slug);
      if (!product) return true; // slug 가 현재 카탈로그에 없음
      return !ProductUtil.getProductPrice({ currency, countryCode, product });
    });
  }

  /**
   * 이 제품의 skuOption 중 **게임키를 포함한 것이 하나라도** 있나 (#5286).
   *
   * 옵션 선택 다이얼로그(`SelectProductOption`)는 `gameResells: []` 로 SKU 를 찾으므로
   * 게임키 skuOption 은 **항상 매칭에 실패**한다(`ProductUtil.getProductSkus` 의
   * `gameResellMatches`). 그래서 이 술어가 참이면 다이얼로그로 보내지 않고 PDP 로 보낸다.
   *
   * ★훅 본문의 조건식이 아니라 **순수함수로** 둔다 — 조건 자체를 단위테스트로 잠글 수
   * 있어야 한다. 훅 안에 두면 뮤턴트(`> 0` → `< 0`, 조건 부정)가 `tsc` 0 · 전체 스위트
   * 초록으로 지나간다(실측: #5348 리뷰 R1 Major). 같은 이유로 이 파일의
   * `getUnresolvedItems` 도 순수함수로 빠져 있다.
   *
   * ⚠️ **`?? []` 를 두지 않는다.** 초판은 *"응답에 필드가 빠지면 터지니 방어"* 로 적었는데
   * 그 전제가 참이면 **이 방어는 세 프레임 뒤에 무효**다 — 술어가 `false` 를 주면 그 제품은
   * 다이얼로그로 가고, 거기서 부르는 `ProductUtil.getSkuByOptions` → `getProductSkus`
   * 에는 방어가 없어 같은 사용자 동작 안에서 던진다(실측). 즉 혼자 막아 봐야 미처리 예외
   * 위치만 옮긴다. 전제도 사실이 아니었다:
   *
   * - 타입이 필수다 — `api/product-api.ts` 의 `SkuOption.gameResells`
   * - prod 카탈로그 **누락 0**. `-L` 필수 — 이 라우트는 리다이렉트다:
   *   `curl -sL "https://www.bhaptics.com/api/products?country=US"`
   *   US·KR 각 36제품 / 58 skuOption · 빈 배열 48 · 값 있음 10 · **필드 누락·null 0**
   *   (dev 도 33제품 / 53 skuOption 에서 0. 수치는 2026-08-24 기준이니 다시 재는 편이 낫다)
   * - **형제 역참조가 전부 `??` 없이 쓴다** — `ProductUtil` 의 `getDeviceBundles` ·
   *   `getProductSkuBySkuOptions` · `getProductSkus` · `getGameBundles`,
   *   `cart-store/bundle-merger` 의 `expandBundlesToComponents` · `makeNonGameBundles` ·
   *   `makeGameBundles`. 여기만 방어하면 표기가 갈라지고 *"이 케이스는 처리됐다"* 는
   *   허위 확신만 남는다(#5348 리뷰 R2 Major).
   *
   *   ★줄번호를 적지 않는다 — 한 줄만 움직여도 통째로 썩고 **썩어도 아무것도 안 깨진다**
   *   (#5348 리뷰 R3 Nit). 지금 몇 곳인지는 자기 트리에서 센다. 이 함수 자신과 store 상태
   *   배열(`state.gameResells`, skuOption 이 아니다)을 빼는 것이 요령이다:
   *   `git grep -nE '\.gameResells\.(length|some|map|filter|flat)' -- 'src/**\/*.ts*'`
   *
   * 그래서 타입·형제 코드와 같은 형태로 둔다. 만약 카탈로그가 필드를 빼기 시작하면
   * 그건 이 함수가 아니라 **경계에서 정규화**할 문제다(호출처를 각각 고치는 것보다 싸다).
   */
  public static hasGameKeySkuOption(product: {
    skuOptions: Array<{ gameResells: unknown[] }>;
  }): boolean {
    return product.skuOptions.some((option) => option.gameResells.length > 0);
  }

  /**
   * 현재 카트 아이템 목록의 총 개수를 반환합니다.
   */
  public static countAllItems(cart: {
    items: Array<{ quantity: number }>;
    gameItems: Array<any>;
  }): number {
    const { items = [], gameItems = [] } = cart;
    const itemsCount = items.reduce((acc, item) => acc + item.quantity, 0);

    return itemsCount + gameItems.length;
  }

  /**
   * Product에 Variant 가 있는지 확인합니다.
   */
  public static hasVariantOptions(product: Product): boolean {
    return product.skuOptions.length > 1 || product.packageDeals.length > 0;
  }

  /**
   * 선택된 옵션이 유효한 SKU와 매칭되는지 확인합니다.
   */
  public static isAllVariantOptionsSelected({
    product,
    options,
  }: {
    product: Product;
    options: CartItemOption[];
  }): boolean {
    // SKU 옵션이 1개 이하면 선택할 게 없음
    if (product.skuOptions.length <= 1) {
      return true;
    }

    const selectedValues = options.map((opt) => opt.value).filter((v) => !!v);

    // 선택된 값이 없으면 false
    if (selectedValues.length === 0) {
      return false;
    }

    // skuOptions의 첫 번째 항목에서 필요한 옵션 개수 확인
    const requiredOptionsCount = product.skuOptions[0]?.options.length ?? 0;

    // "all-size" 특수 케이스
    if (selectedValues.includes("all-size")) {
      // all-size 제외한 나머지 옵션 개수가 requiredOptionsCount - 1 이상이면 OK
      return selectedValues.length >= requiredOptionsCount;
    }

    // 필요한 옵션 개수만큼 선택되었는지 확인
    return selectedValues.length >= requiredOptionsCount;
  }

  private static hasItemInCart(
    targets: ProductSlug[],
    cartItems: CartItem[],
  ): boolean {
    return cartItems.some((item) => targets.includes(item.slug));
  }
}
